import type {EnumLike} from 'zod';
import {z} from 'zod';
import {ContractType, ProjectStatus, SortType, ProjectClientAction, SortOrder, VerticalType, OpportunityType, RenewalStatus} from '@api';
import {PaymentRedirectionSuccessTypes} from '../types';

// - Input

const queryStringToArray = () =>
    z
        .union([z.string(), z.array(z.string())])
        .transform((arg) => (typeof arg === 'string' ? [arg] : arg))
        .catch([]);

// Filter non-valid enums
const safeEnumArray = <T extends EnumLike>(value: T) =>
    z.array(z.nativeEnum(value).or(z.string())).transform((ar, ctx) => {
        return ar.filter((f): f is T[keyof T] => z.nativeEnum(value).safeParse(f).success);
    });

const optionalStringParam = () => z.string().optional().catch(undefined);

export const projectListRouteFiltersSchema = z.object({
    page: z.coerce.number().min(0).catch(0),
    size: z.coerce.number().min(1).max(1000).catch(20),
    actions: queryStringToArray().pipe(safeEnumArray(ProjectClientAction)),
    status: queryStringToArray().pipe(safeEnumArray(ProjectStatus)),
    owners: queryStringToArray(),
    companies: queryStringToArray(),
    organizations: queryStringToArray(),
    freelancers: queryStringToArray(),
    companyManagers: queryStringToArray(),
    finOpsManagers: queryStringToArray(),
    customerSuccessManagers: queryStringToArray(),
    contractTypes: queryStringToArray().pipe(safeEnumArray(ContractType)),
    verticalTypes: queryStringToArray().pipe(safeEnumArray(VerticalType)),
    opportunityTypes: queryStringToArray().pipe(safeEnumArray(OpportunityType)),
    renewalStatuses: queryStringToArray().pipe(safeEnumArray(RenewalStatus)),
    startDateFrom: z.coerce.date().optional().catch(undefined),
    startDateTo: z.coerce.date().optional().catch(undefined),
    endDateFrom: z.coerce.date().optional().catch(undefined),
    endDateTo: z.coerce.date().optional().catch(undefined),
    query: optionalStringParam(),
    sortBy: z.nativeEnum(SortType).catch(SortType.Action),
    sortOrder: z.nativeEnum(SortOrder).catch(SortOrder.Asc),
    companyId: optionalStringParam(),
    forceNavigate: z
        .string()
        .transform((value) => {
            return value === 'true';
        })
        .optional()
        .catch(undefined),
});

// - Output

export const routeFiltersToQueryParamsSchema = projectListRouteFiltersSchema.merge(
    z.object({
        startDateFrom: z.coerce.string().optional(),
        startDateTo: z.coerce.string().optional(),
        endDateFrom: z.coerce.string().optional(),
        endDateTo: z.coerce.string().optional(),
        forceNavigate: z.coerce.string().optional(),
    }),
);
